import { supabase } from './supabase';

/**
 * Load every shop the master_admin can see (RLS allows because
 * is_platform_admin(auth.uid()) policy gives access to all rows).
 * Returns enriched rows with computed stats.
 */
export const loadAllShops = async () => {
  const { data: shops, error } = await supabase
    .from('shops')
    .select('id,name,subdomain,plan,logo_url,accent_colour,stripe_account_id,stripe_subscription_id,subscription_status,subscription_interval,current_period_end,cancel_at_period_end,created_at,welcome_text');
  if (error) {
    // Throw so the page surfaces the failure instead of pretending there
    // are zero shops. Most likely cause: a pending Supabase migration.
    console.error('loadAllShops failed:', error);
    throw error;
  }
  return shops || [];
};

// Compute MRR contribution for a single shop (£/month).
export const mrrFor = (shop, settings) => {
  if (!shop.stripe_subscription_id || !['active', 'trialing'].includes(shop.subscription_status)) return 0;
  const std = Number(settings?.standard_price || 19);
  const prem = Number(settings?.premium_price || 49);
  const disc = Number(settings?.annual_discount_pct || 10) / 100;
  const base = shop.plan === 'premium' ? prem : std;
  if (shop.subscription_interval === 'annual') {
    // Annualised price ÷ 12
    return (base * 12 * (1 - disc)) / 12;
  }
  return base;
};

// Onboarding stage derivation for a shop. Returns one of:
// 'account_created' | 'profile_set_up' | 'stripe_kyc' | 'live'
export const onboardingStage = (shop, hasServices) => {
  const hasLiveSub = shop.subscription_status === 'active' || shop.subscription_status === 'trialing';
  const hasKyc    = !!shop.stripe_account_id; // best-effort signal (full check would need Stripe API)
  const hasProfile = !!(shop.name && shop.welcome_text && hasServices);
  if (hasLiveSub) return 'live';
  if (hasKyc)     return 'stripe_kyc';
  if (hasProfile) return 'profile_set_up';
  return 'account_created';
};

// Compute health flags for a shop given:
//  - last_login (auth.users.last_sign_in_at)
//  - bookingsThisWeek / bookingsLastWeek (counts)
//  - subscription_status
// Returns { band: 'green'|'amber'|'red', signals: string[] }
export const healthFor = ({ subscriptionStatus, lastLoginAt, bookingsThisWeek, bookingsLastWeek }) => {
  const signals = [];
  let band = 'green';
  const now = Date.now();

  if (subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid' || subscriptionStatus === 'incomplete') {
    signals.push('failed_payment');
    band = 'red';
  }
  if (lastLoginAt) {
    const days = (now - new Date(lastLoginAt).getTime()) / 86400000;
    if (days > 14) {
      signals.push('no_login_14d');
      band = band === 'red' ? 'red' : 'amber';
    }
  }
  if (bookingsLastWeek > 0) {
    const drop = (bookingsLastWeek - bookingsThisWeek) / bookingsLastWeek;
    if (drop > 0.3) {
      signals.push('bookings_dropped_30');
      band = band === 'red' ? 'red' : 'amber';
    }
  }
  return { band, signals };
};

// Brand colour ↔ avatar (first letter on a colour swatch)
const COLOUR_PALETTE = ['#4f46ff', '#10b981', '#f59e0b', '#b03a2e', '#0e0f13', '#7d77dd', '#1f2a1e'];
export const colourForShop = (shop) => {
  if (shop.accent_colour) return shop.accent_colour;
  // Stable hash on id
  const s = shop.id || shop.name || '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return COLOUR_PALETTE[Math.abs(h) % COLOUR_PALETTE.length];
};
